/**
 * A single H.264 NAL unit extracted from an Annex B stream.
 *
 * - `type` is `nal_unit_type` (the low 5 bits of the NAL header byte), in the
 *   range `0..31`. See ISO/IEC 14496-10 Table 7-1 (e.g. 5 = IDR, 7 = SPS,
 *   8 = PPS).
 * - `data` is the raw NAL bytes including the 1-byte NAL header. The Annex B
 *   start code (`00 00 01` / `00 00 00 01`) is NOT included. Emulation
 *   prevention bytes (`0x03` after `00 00`) are NOT stripped — callers that
 *   need to parse the NAL payload (e.g. SPS RBSP) must handle that themselves.
 */
export interface NALUnit {
  type: number;
  data: Uint8Array;
}

// H.264 NAL unit types (subset we care about). See ISO/IEC 14496-10 Table 7-1.
export const NAL_TYPE = {
  NON_IDR: 1,
  IDR: 5,
  SEI: 6,
  SPS: 7,
  PPS: 8,
  AUD: 9,
} as const;

// VCL NAL unit types are 1..5 inclusive — these carry coded slice data.
export const VCL_NAL_TYPE_MIN = 1;
export const VCL_NAL_TYPE_MAX = 5;

// Bit masks for the 1-byte NAL header.
export const NAL_TYPE_MASK = 0x1f; // nal_unit_type bits (low 5)
export const FORBIDDEN_ZERO_BIT_MASK = 0x80; // must be 0 per spec

// Length of a matched Annex B start code at a given offset, or 0 if none.
type StartCodeLength = 0 | 3 | 4;

function matchStartCode(data: Uint8Array, i: number): StartCodeLength {
  if (data[i] !== 0 || data[i + 1] !== 0) return 0;
  if (data[i + 2] === 1) return 3;
  if (i + 3 < data.length && data[i + 2] === 0 && data[i + 3] === 1) return 4;
  return 0;
}

/**
 * Parse an H.264 Annex B byte stream into NAL units.
 *
 * Walks `data` once, splitting on start codes (`00 00 01` or `00 00 00 01`)
 * and emitting one `NALUnit` per chunk between start codes.
 *
 * Each returned `NALUnit.data` is a `subarray` view over `data` — no bytes
 * are copied. This means the caller MUST NOT reuse / mutate the underlying
 * buffer while it still holds references to the returned NAL units. Today
 * the only caller wraps a fresh `MessageEvent.data` ArrayBuffer per message,
 * so this is safe.
 *
 * Non-goals (intentionally not handled here):
 * - No emulation-prevention byte (`0x03`) removal.
 * - No validation of `forbidden_zero_bit` or other NAL header fields — that
 *   is the caller's responsibility (see `FORBIDDEN_ZERO_BIT_MASK`).
 * - HEVC / H.265 NAL header layout is different and is not supported.
 */
export function parseAnnexB(data: Uint8Array): NALUnit[] {
  const nals: NALUnit[] = [];
  // Offset of the current NAL payload (after a matched start code), or -1 before
  // the first start code is seen.
  let nalStart = -1;

  let i = 0;
  while (i + 2 < data.length) {
    const sc = matchStartCode(data, i);
    if (sc === 0) {
      i++;
      continue;
    }

    // Close the previous NAL (if any) at the byte before this start code.
    if (nalStart >= 0 && i > nalStart) {
      nals.push(makeNal(data.subarray(nalStart, i)));
    }

    i += sc;
    nalStart = i;
  }

  // Trailing NAL: everything after the last start code up to end of buffer.
  if (nalStart >= 0 && nalStart < data.length) {
    nals.push(makeNal(data.subarray(nalStart)));
  }

  return nals;
}

function makeNal(data: Uint8Array): NALUnit {
  return { type: data[0] & NAL_TYPE_MASK, data };
}
